import { useEffect, useMemo, useState } from 'react';
import type { SubTable } from '../../domain';
import type { TableAdapter } from '../../ports';

export interface SubTableState {
    childAdapter: TableAdapter<unknown> | null;
    childSelectedRows: unknown[];
    setChildSelectedRows: (rows: unknown[]) => void;
}

export interface UseSubTableArgs<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see TableProps.subTable for why `any`, not `unknown`
    subTable?: SubTable<T, any>;
    expandedId: string | null;
    rows: T[];
    getRowId: (row: T) => string;
    toAdapter: (data: unknown) => TableAdapter<unknown>;
}

export function useSubTable<T>(args: UseSubTableArgs<T>): SubTableState {
    const { subTable, expandedId, rows, getRowId, toAdapter } = args;

    const [childSelectedRows, setChildSelectedRows] = useState<unknown[]>([]);
    useEffect(() => {
        setChildSelectedRows([]);
        subTable?.onSelectionChange?.([], []);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset belongs to a change of expanded row, not to identity changes of the sub-table config
    }, [expandedId]);

    const childAdapter = useMemo<TableAdapter<unknown> | null>(() => {
        if (!subTable || expandedId === null) return null;
        const row = rows.find((r) => getRowId(r) === expandedId);
        if (!row) return null;
        const data = subTable.data(row);
        return toAdapter(data);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one adapter per expanded id; an adapter is stateful and must not be rebuilt while the same row stays expanded
    }, [subTable, expandedId]);

    return { childAdapter, childSelectedRows, setChildSelectedRows };
}
